import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import { Tooltip } from '@neo/primitives/tooltip'
import { useStore } from '@xyflow/react'
import { useState } from 'react'
import { useTypedReactFlow } from '../../hooks/use-react-flow'
import { useFlowStore } from '../../store'
import {
  hasInvalidLeaderProfile,
  isSwitchable,
  LEADER_PROFILE_V1,
  SYSTEM_ONE_THINKING_INVALID,
  stampLeaderProfile,
} from '../../utils/system-one-thinking'

// Unlike the other header buttons this one never fills blue on hover: a grey tint instead.
const calmHover = 'hover:border-input hover:bg-accent hover:text-accent-foreground'
// In every look, so rings still playing after a Cancel are cut off at the button's edge too.
const clipped = 'relative overflow-hidden'
const onLook = cn(
  'bg-rainbow bg-[length:200%_100%] animate-rainbow motion-reduce:animate-none',
  // The hover: twins keep the text navy and the edge clear over the rainbow.
  'border-transparent text-[#0f1729] hover:border-transparent hover:text-[#0f1729]',
)
const rings = ['[animation-delay:0ms]', '[animation-delay:250ms]', '[animation-delay:500ms]']
// Pixel art: each 5px cell takes the colour of its middle pixel, so a round ring is a staircase.
const pixels = (
  <svg aria-hidden="true" className="absolute h-0 w-0">
    <filter id="system-one-pixels" x="0" y="0" width="100%" height="100%">
      <feFlood x="2" y="2" width="1" height="1" />
      <feComposite width="5" height="5" />
      <feTile result="cells" />
      <feComposite in="SourceGraphic" in2="cells" operator="in" />
      <feMorphology operator="dilate" radius="2" />
    </filter>
  </svg>
)

export const SystemOneThinkingSwitch = () => {
  const leaderProfile = useFlowStore((state) => state.leaderProfile)
  const canSwitch = useStore((state) => state.nodes.some(isSwitchable))
  const isInvalid = useStore((state) => hasInvalidLeaderProfile(state.nodes))
  const { setNodes } = useTypedReactFlow()
  // Above 0 while the rings are on the page. A new value remounts them, so they play again.
  const [ripple, setRipple] = useState(0)

  if (!canSwitch && !isInvalid) return null

  const switchTo = (isOn: boolean) => {
    const next = isOn ? LEADER_PROFILE_V1 : null
    useFlowStore.getState().setLeaderProfile(next)
    setNodes((nodes) => stampLeaderProfile(nodes, next))
    useFlowStore.getState().markUserChange()
  }
  // An invalid saved profile reads as on, so the first click removes it.
  const isOn = isInvalid || leaderProfile !== null
  const toggle = () => {
    if (!useFlowStore.getState().isEditing) {
      // The Edit button decides who may edit and when, so view mode goes through it.
      document
        .querySelector<HTMLButtonElement>('[data-testid="button-edit-flow"]:not(:disabled)')
        ?.click()
      if (!useFlowStore.getState().isEditing) return
    }
    setRipple(isOn ? 0 : ripple + 1)
    switchTo(!isOn)
  }

  return (
    // The Button's own tooltip props re-create the button when the hint comes or goes.
    <Tooltip content={isInvalid ? SYSTEM_ONE_THINKING_INVALID : ''}>
      <div className="ml-1 flex shrink-0 border-l border-st2 pl-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          // An invalid profile keeps the plain button: its red edge cannot be seen on the rainbow.
          className={cn(
            clipped,
            calmHover,
            isOn && !isInvalid && onLook,
            isInvalid &&
              'border-destructive text-destructive hover:border-destructive hover:text-destructive',
          )}
          onClick={toggle}
          aria-pressed={isOn}
          aria-invalid={isInvalid}
          data-testid="switch-system-one-thinking"
        >
          {ripple > 0 && (
            <span
              aria-hidden
              className={cn(
                'pointer-events-none absolute inset-0 motion-reduce:hidden',
                '[filter:url(#system-one-pixels)]',
              )}
            >
              {pixels}
              {rings.map((delay, place) => (
                <span
                  key={delay + ripple}
                  // The last ring takes them all out, or a button that comes back later would replay them.
                  onAnimationEnd={place === rings.length - 1 ? () => setRipple(0) : undefined}
                  className={cn(
                    // A circle from the centre: width is a share of the button's, height follows.
                    'absolute left-1/2 top-1/2 aspect-square rounded-full',
                    '-translate-x-1/2 -translate-y-1/2 border-[5px] border-[#da997b] opacity-0',
                    'animate-ripple',
                    delay,
                  )}
                />
              ))}
            </span>
          )}
          {/* Positioned, so the label is painted over the rings and stays readable. */}
          <span className="relative">System One Thinking</span>
        </Button>
      </div>
    </Tooltip>
  )
}
